import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Mail, Phone, MapPin } from 'lucide-react';
import { FlagImg } from '../FlagImg';

const languages = [
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'nl', label: 'NL' },
];

export const Footer = () => {
  const { t, language, changeLanguage } = useLanguage();

  return (
    <footer className="bg-slate-950 text-slate-400" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="font-heading text-2xl font-bold text-white tracking-tight">
              TRH<span className="text-green-500">STEEL</span>
            </h3>
            <p className="text-sm leading-relaxed">
              {t('footerTagline')}
            </p>
            <div className="flex items-center space-x-1 pt-4">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`px-2 py-1 text-sm transition-colors ${
                    language === lang.code
                      ? 'text-white bg-slate-800'
                      : 'text-slate-500 hover:text-white'
                  }`}
                  data-testid={`footer-lang-${lang.code}`}
                >
                  <FlagImg code={lang.code} size={20} />
                </button>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-heading text-lg font-bold text-white uppercase tracking-wider">
              {t('home')}
            </h4>
            <nav className="flex flex-col space-y-2">
              <Link to="/" className="text-sm hover:text-white transition-colors" data-testid="footer-home">
                {t('home')}
              </Link>
              <Link to="/about" className="text-sm hover:text-white transition-colors" data-testid="footer-about">
                {t('aboutUs')}
              </Link>
              <Link to="/products" className="text-sm hover:text-white transition-colors" data-testid="footer-products">
                {t('products')}
              </Link>
              <Link to="/sustainability" className="text-sm hover:text-white transition-colors" data-testid="footer-sustainability">
                {t('sustainability')}
              </Link>
              <Link to="/quality" className="text-sm hover:text-white transition-colors" data-testid="footer-quality">
                {t('quality')}
              </Link>
              <Link to="/news" className="text-sm hover:text-white transition-colors" data-testid="footer-news">
                {t('news')}
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="font-heading text-lg font-bold text-white uppercase tracking-wider">
              {t('contact')}
            </h4>
            <div className="flex flex-col space-y-3">
              <a 
                href="mailto:info@trhsteel.com" 
                className="flex items-center space-x-2 text-sm hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail className="h-4 w-4" />
                <span>info@trhsteel.com</span>
              </a>
              <div className="flex items-start space-x-2 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('footerSeville')}</span>
              </div>
              <div className="flex items-start space-x-2 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('footerLiege')}</span>
              </div>
            </div>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h4 className="font-heading text-lg font-bold text-white uppercase tracking-wider">
              {t('legal')}
            </h4>
            <nav className="flex flex-col space-y-2">
              <Link to="/legal" className="text-sm hover:text-white transition-colors" data-testid="footer-legal">
                {t('legalNotice')}
              </Link>
              <Link to="/privacy" className="text-sm hover:text-white transition-colors" data-testid="footer-privacy">
                {t('privacyPolicy')}
              </Link>
              <Link to="/cookies" className="text-sm hover:text-white transition-colors" data-testid="footer-cookies">
                {t('cookies')}
              </Link>
              <Link to="/terms" className="text-sm hover:text-white transition-colors" data-testid="footer-terms">
                {t('generalTerms')}
              </Link>
              <button
                onClick={() => window.openCookieSettings && window.openCookieSettings()}
                className="text-sm hover:text-white transition-colors text-left"
                data-testid="footer-cookie-settings"
              >
                Cookie Settings
              </button>
            </nav>
          </div>
        </div>

        {/* EU Funding Notice - Pyme Digital */}
        <div className="mt-10 pt-6 border-t border-slate-800/50">
          {/* Institutional Logos Row */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 mb-5 py-3">
            {/* 1. EU Flag + Cofinanciado */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <svg width="40" height="28" viewBox="0 0 810 540" xmlns="http://www.w3.org/2000/svg">
                <rect width="810" height="540" fill="#003399"/>
                {[...Array(12)].map((_, i) => {
                  const angle = (i * 30 - 90) * Math.PI / 180;
                  const cx = 405 + 140 * Math.cos(angle);
                  const cy = 270 + 140 * Math.sin(angle);
                  return <polygon key={i} points={[...Array(5)].map((__, j) => {
                    const a = (j * 144 - 90) * Math.PI / 180;
                    return `${cx + 18 * Math.cos(a)},${cy + 18 * Math.sin(a)}`;
                  }).join(' ')} fill="#FFCC00"/>;
                })}
              </svg>
              <div className="text-[9px] text-slate-400 leading-tight">
                <span className="font-semibold">Cofinanciado por</span><br/>la Unión Europea
              </div>
            </div>
            {/* 2. Ministerio de Hacienda */}
            <div className="bg-white rounded px-3 py-1.5 flex-shrink-0">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/images/pyme-digital/logo_ministerio_user.png`}
                alt="Ministerio de Hacienda"
                className="h-9"
              />
            </div>
            {/* 3. Fondos Europeos */}
            <div className="bg-white rounded px-3 py-1.5 flex-shrink-0">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/images/pyme-digital/logo_fondos_v2.png`}
                alt="Fondos Europeos"
                className="h-9"
              />
            </div>
            {/* 4. Cámara de Comercio de España */}
            <div className="bg-white rounded px-3 py-1.5 flex-shrink-0">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/images/pyme-digital/logo_camara_user.jpg`}
                alt="Cámara de Comercio de España"
                className="h-9"
              />
            </div>
          </div>
          {/* Text */}
          <p className="text-[10px] text-slate-500 leading-relaxed text-center max-w-4xl mx-auto">
            <strong className="text-slate-400">TRH ZF SEVILLA S.L.</strong> ha sido beneficiaria de Fondos Europeos, cuyo objetivo es la mejora de la competitividad de las PYMES, y gracias al cual ha puesto en marcha un Plan de Acción con el objetivo de reforzar la digitalización y la competitividad de las pymes durante el año 2025. Para ello ha contado con el apoyo del <strong className="text-slate-400">Programa Pyme Digital</strong> de la <strong className="text-slate-400">Cámara de Comercio de Sevilla</strong>. <span className="text-slate-400">#EuropaSeSiente</span>
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} TRH Steel. {t('allRightsReserved')}.
            </p>
            <p className="text-xs text-slate-600 font-mono">
              European steel, sustainable commitment.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
